import { useMemo } from "react";
import type { UpdateTerminSlot } from "../../hooks/usePlanung";
import type { Kunde, Owner, TeamMember, Termin } from "../../lib/types";

export const MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

export const WD = ["Mo","Di","Mi","Do","Fr","Sa","So"];

export const WD_FULL: Record<string, string> = { Mo: "Montag", Di: "Dienstag", Mi: "Mittwoch", Do: "Donnerstag", Fr: "Freitag", Sa: "Samstag", So: "Sonntag" };

export const WD_LONG = ["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"];

export function firstOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }

export function mondayOf(d: Date) { const x=new Date(d.getFullYear(),d.getMonth(),d.getDate()); x.setDate(x.getDate()-((x.getDay()+6)%7)); return x; }

export function addDays(d: Date, n: number) { const x=new Date(d); x.setDate(x.getDate()+n); return x; }

export function iso(d: Date) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }

export function hhmm(s: string) { const d=new Date(s); return Number.isNaN(d.getTime())?"":d.toLocaleTimeString("de-DE",{hour:"2-digit",minute:"2-digit"}); }

export function h1(n: number) { return (n||0).toLocaleString("de-DE",{maximumFractionDigits:1}); }

export function fmtPause(x: number): string { if (!x || x <= 0) return "0"; const m = Math.round(x*60); const hh = Math.floor(m/60); const mm = m%60; return hh>0 ? `${hh}:${String(mm).padStart(2,"0")}` : `${mm}m`; }

export function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const HELFER_COLORS = ["var(--vg-accent)","var(--vg-green)","#3a86a8","var(--vg-amber)","#9a6a3a","#4a7a6a","#8a5a9a","#a8563e"];

export function colorFor(id: string, role: "helfer" | "inhaber"): string {
  if (role === "inhaber") return "#6b3fa0";
  let h = 0; for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return HELFER_COLORS[h % HELFER_COLORS.length];
}

export function nettoFor(datum: string, von: string, bis: string, pause: number): number {
  const a = new Date(`${datum}T${von}:00`).getTime();
  const b = new Date(`${datum}T${bis}:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, (b - a) / 3_600_000 - (pause || 0));
}

export function grossFor(datum: string, von: string, bis: string): number {
  const a = new Date(`${datum}T${von}:00`).getTime();
  const b = new Date(`${datum}T${bis}:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, (b - a) / 3_600_000);
}

export function defaultPause(gross: number): number { return gross >= 5 ? 0.5 : 0; }

export const SL_STATUS: Record<string, { bg: string; fg: string; label: string }> = {
  bestaetigt: { bg: "var(--vg-green-soft)", fg: "var(--vg-green)", label: "bestätigt" },
  geplant: { bg: "var(--vg-bg-4)", fg: "var(--vg-text-3)", label: "geplant" },
  pending_helfer: { bg: "var(--vg-amber-soft)", fg: "var(--vg-amber)", label: "wartet" },
  pending_inhaber: { bg: "var(--vg-amber-soft)", fg: "var(--vg-amber)", label: "wartet" },
  abgelehnt: { bg: "var(--vg-red-soft)", fg: "var(--vg-red)", label: "abgelehnt" },
};

export function statusOf(cs: string) { return SL_STATUS[cs] || SL_STATUS.geplant; }

export const EINSATZ_STATUS: Record<string, { bg: string; fg: string; label: string }> = {
  wartet_auf_helfer: { bg: "var(--vg-amber-soft)", fg: "var(--vg-amber)", label: "Einladung offen" },
  wartet_auf_inhaber: { bg: "var(--vg-blue-soft)", fg: "var(--k-on-blue-soft)", label: "Bewerbung offen" },
  eingeladen: { bg: "var(--vg-blue-soft)", fg: "var(--k-on-blue-soft)", label: "Bewerbung offen" },
  zugesagt: { bg: "var(--vg-green-soft)", fg: "var(--vg-green)", label: "zugesagt" },
  abgesagt_von_helfer: { bg: "var(--vg-red-soft)", fg: "var(--vg-red)", label: "abgesagt" },
  abgelehnt_vom_inhaber: { bg: "var(--vg-red-soft)", fg: "var(--vg-red)", label: "abgelehnt" },
};

export function einsatzStatusOf(es: string) { return EINSATZ_STATUS[es] || EINSATZ_STATUS.wartet_auf_helfer; }

/**
 * Der Ort steht als Freitext am Projekt („Hauptstraße 12, Dresden", manchmal
 * nur „Radebeul"). Deshalb die Maps-*Suche* statt einer Koordinate — die
 * findet auch unvollständige Angaben, während eine Geokodierung hier nur eine
 * zusätzliche Fehlerquelle wäre.
 */
export function mapsUrl(ort: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ort)}`;
}

export type Person = { id: string; name: string; role: "helfer" | "inhaber"; rate: number; color: string };

export function usePeople(members: TeamMember[], owners: Owner[]): Person[] {
  return useMemo(() => {
    const inh: Person[] = owners.map((o) => ({ id: o.id, name: o.name, role: "inhaber" as const, rate: 0, color: colorFor(o.id, "inhaber") }));
    const ownerIds = new Set(owners.map((o) => o.id));
    const ownerNames = new Set(owners.map((o) => o.name.trim().toLowerCase()));
    const hel: Person[] = members
      .filter((m) => m.active !== false && m.role !== "inhaber" && !ownerIds.has(m.id) && !ownerNames.has((m.name || "").trim().toLowerCase()))
      .map((m) => ({ id: m.id, name: m.name, role: "helfer" as const, rate: m.hourly_rate || 0, color: colorFor(m.id, "helfer") }));
    hel.sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
    return [...inh, ...hel];
  }, [members, owners]);
}

export type Pick = {
  slot_id?: string;
  von: string; bis: string; stundenlohn: number; pause: number; note: string;
  confirm_status: string; einladungs_status: string;
  /* Nicht im Planer bearbeitbar, aber mitgeführt — sonst gingen sie beim
     Speichern verloren. Siehe picksToSlots. */
  zusatzkosten: number;
  verguetungsart: "stundenlohn" | "pauschale";
  pauschale_id: string | null;
};

export type Picked = Record<string, Pick>;

/**
 * Auswahl → Slot-Liste für PATCH /planung/{id}.
 *
 * Wichtig: hier werden auch Felder mitgeführt, die der Planer gar nicht
 * bearbeitet — Zusatzkosten, Vergütungsart und Festpreis-Verknüpfung. Früher
 * stand hier `zusatzkosten: 0` und die beiden anderen fehlten ganz; einmal
 * einen Termin öffnen und speichern machte damit aus einem Pauschal-Einsatz
 * einen Stundenlohn-Einsatz und setzte Auslagen auf null.
 */
/** Slots eines bestehenden Termins fuer eine Kopie auf einen anderen Tag.
 *
 * Uhrzeit und Dauer je Person bleiben, nur das Datum wandert. Bewusst NICHT
 * mitkopiert wird der Bestaetigungsstand: eine frisch angelegte Kopie gilt als
 * `geplant`, sonst waeren nachgetragene Stunden schon bestaetigt, ohne dass sie
 * je jemand gesehen haette.
 */
export function kopierteSlots(t: Termin, zielDatum: string): UpdateTerminSlot[] {
  return t.slots.map((sl) => ({
    person_id: sl.person_id,
    person_typ: sl.person_typ,
    start_at: `${zielDatum}T${(sl.start_at || "").slice(11, 19) || "08:00:00"}`,
    end_at: `${zielDatum}T${(sl.end_at || "").slice(11, 19) || "16:00:00"}`,
    pause_stunden: sl.pause_stunden || 0,
    stundenlohn: sl.stundenlohn || 0,
    zusatzkosten: sl.zusatzkosten || 0,
    verguetungsart: sl.verguetungsart === "pauschale" ? "pauschale" : "stundenlohn",
    pauschale_id: sl.pauschale_id ?? null,
    note: sl.note || "",
    confirm_status: "geplant",
    einladungs_status: sl.person_typ === "helfer" ? "wartet_auf_helfer" : "zugesagt",
  }));
}

export function picksToSlots(picked: Picked, people: Person[], datum: string): UpdateTerminSlot[] {
  return Object.entries(picked).map(([id, p]) => {
    const typ = people.find((x) => x.id === id)?.role ?? "helfer";
    return {
      person_id: id, person_typ: typ,
      start_at: `${datum}T${p.von}:00`, end_at: `${datum}T${p.bis}:00`,
      pause_stunden: p.pause || 0, stundenlohn: typ === "inhaber" ? 0 : (p.stundenlohn || 0),
      zusatzkosten: p.zusatzkosten || 0, note: p.note || "",
      verguetungsart: p.verguetungsart, pauschale_id: p.pauschale_id,
      confirm_status: p.confirm_status || undefined,
      einladungs_status: p.einladungs_status || (typ === "helfer" ? "wartet_auf_helfer" : "zugesagt"),
    };
  });
}

/** Geplante Personalkosten. Pauschal-Einsätze zählen hier NICHT mit — ihr
 *  Festpreis hängt am Projekt, nicht an den Stunden dieses Termins. */
export function pickedCost(picked: Picked, people: Person[], datum: string): number {
  return Object.entries(picked).reduce((s, [id, p]) => {
    const per = people.find((x) => x.id === id); if (!per || per.role === "inhaber") return s;
    if (p.verguetungsart === "pauschale") return s;
    return s + nettoFor(datum, p.von, p.bis, p.pause) * (p.stundenlohn || 0);
  }, 0);
}

/* Abgeschlossene/verlorene Projekte stehen jetzt mit in der Auswahl — fuer die
   Nachpflege von Stunden ist genau das noetig. Damit niemand versehentlich auf
   einer beendeten Baustelle plant, tragen sie einen sichtbaren Vermerk. */
export function istRuhend(status: string): boolean {
  const s = (status || "").toLowerCase();
  return ["abgeschlossen", "verloren", "archiviert", "abgelehnt"].some((x) => s.includes(x));
}

/** Kundenadresse einzeilig — Grundlage des Maps-Links bei Besichtigungen. */
export function kundenOrt(k: Kunde): string {
  return [k.strasse, [k.plz, k.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ").trim();
}

/** Besichtigung = Termin am Kunden, noch ohne Projekt. */
export function istBesichtigung(t: { planung_typ?: string; projekt_id?: string }): boolean {
  return (t.planung_typ || "") === "besichtigung" || !t.projekt_id;
}

/**
 * Einen eben angelegten Datensatz vor die Serverliste hängen, bis sie ihn
 * selbst kennt.
 *
 * Ohne das steht das Feld nach dem Anlegen für einen Moment leer: die ID ist
 * gesetzt, aber die Combo sucht sie in einer Liste, die noch vom vorigen
 * Abruf stammt. Sobald der Refetch da ist, greift die Dublettenprüfung und
 * der Vorhang fällt von selbst.
 */
export function mitFrischem<T extends { id: string }>(liste: T[], frisch: T | null): T[] {
  if (!frisch || liste.some((x) => x.id === frisch.id)) return liste;
  return [frisch, ...liste];
}

/**
 * Terminzeit geändert — die eingeteilten Personen mitziehen.
 *
 * Wer bisher **genau** zur Terminzeit eingeteilt war, folgt der neuen: das ist
 * der Normalfall, und ihn von Hand nachzuziehen war die eigentliche Zumutung.
 * Wer eine abweichende Zeit hatte, behält sie — diese Abweichung hat jemand
 * absichtlich eingetragen (der Helfer, der erst mittags dazukommt), und ein
 * pauschales Überschreiben würde sie stillschweigend löschen.
 *
 * Daraus folgt die Bedienregel von selbst: eine Person, deren Zeit einmal
 * eigens gesetzt wurde, hängt sich vom Termin ab und bleibt eigenständig.
 *
 * Die Pause bleibt unangetastet. Sie aus der neuen Dauer neu zu berechnen
 * würde eine von Hand gesetzte Pause überschreiben, und welche der beiden
 * gemeint ist, kann hier niemand wissen.
 */
export function zeitenMitziehen(
  picked: Picked, altVon: string, altBis: string, neuVon: string, neuBis: string,
): Picked {
  if (altVon === neuVon && altBis === neuBis) return picked;
  let geaendert = false;
  const naechster: Picked = {};
  for (const [id, p] of Object.entries(picked)) {
    if (p.von === altVon && p.bis === altBis) {
      naechster[id] = { ...p, von: neuVon, bis: neuBis };
      geaendert = true;
    } else {
      naechster[id] = p;
    }
  }
  return geaendert ? naechster : picked;
}

/** Wie viele Eingeteilte eine vom Termin abweichende Zeit haben. */
export function mitEigenerZeit(picked: Picked, von: string, bis: string): number {
  return Object.values(picked).filter((p) => p.von !== von || p.bis !== bis).length;
}

/** /planung?datum=JJJJ-MM-TT — z. B. „Termin öffnen" im Projektkopf: Monat und Tag stehen gleich dort. */
export function datumAusLink(): string {
  try {
    const v = new URLSearchParams(window.location.search).get("datum") || "";
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "";
  } catch {
    return "";
  }
}

export function istSchmal(): boolean {
  try { return window.matchMedia("(max-width: 760px)").matches; } catch { return false; }
}
